import Phaser from "phaser";
import { LevelMeta, levelById } from "../levels";
import { GameState, LevelId } from "../state/GameState";
import { Palette } from "../ui/palette";
import { Tex, collectibleTex, ensureSheepyAnim, SHEEPY_ANIM } from "../ui/textures";
import { TILESET_NAME, TILESET_KEY, TILESET_SHEET_KEY, Prop } from "../tiles";
import { renderTiledAuthoredContent, createTiledText } from "../ui/tiledRender";
import { buildMidFloorBodies } from "../ui/midFloor";
import { baseValueForKind, throwMultiplier, speedBonus, SHEEPY_BONUS } from "../state/scoring";

type TiledObject = Phaser.Types.Tilemaps.TiledObject;

/** Tilemap cache key of the shared, Tiled-authored HUD (public/assets/maps/hud.tmj),
 *  reused by every level. A level can override it with its own "hud" object layer. */
const SHARED_HUD_KEY = "hud";
/** Height (px) of the translucent bar pinned across the top of every level. It
 *  darkens the art behind the HUD so the score, status line and timer stay legible.
 *  Kept compact: each corner readout is a tight two-line stack. */
const HUD_BAR_H = 40;
/** Highlight yellow for the big HUD countdown (levels that run on a timer). */
const HUD_YELLOW = "#ffd23f";

// On-screen size (px) a collectible normalizes to, by kind. Most sit at the
// default; laptop and VW read bigger so they stand out as grab-worthy props.
const COLLECTIBLE_PX_DEFAULT = 24;
const COLLECTIBLE_PX: Record<string, number> = {
  laptop: 36,
  vw: 36,
  money: 48, // bumped for the bigger Dan / larger world
  feature: 52, // the Claude-logo "feature request" bubbles read big and grab-worthy
};

/** On-screen size (px) a collectible of `kind` normalizes to. Single source of
 *  truth so code that spawns collectibles outside spawnCollectible (e.g. the PE
 *  Guy's dropped money) stays in sync. */
export function collectiblePx(kind: string): number {
  return COLLECTIBLE_PX[kind] ?? COLLECTIBLE_PX_DEFAULT;
}

/** A door barrier. There is NO wall tile behind a door - the sprite IS the
 *  barrier. Opening it destroys the sprite so the player walks through open
 *  space. `opensWith` is a key kind, "switch", or "vault". */
export interface DoorRec {
  opensWith: string;
  sprite: Phaser.Physics.Arcade.Image;
  colliders: Phaser.Physics.Arcade.Collider[]; // player + any boxes; all torn down on open
  opened: boolean;
  x: number; // world x of the doorway (sprite gets destroyed on open)
  counted: boolean; // whether "passing through" has been scored yet
}

/** Read a custom property off a Tiled object, tolerant of the array form (the
 *  JSON export) and the flattened object form (some Phaser versions). */
export function objProp<T>(obj: TiledObject, name: string, fallback: T): T {
  const props = (obj as unknown as { properties?: unknown }).properties;
  if (!props) return fallback;
  if (Array.isArray(props)) {
    const hit = props.find((p) => (p as { name: string }).name === name) as
      | { value: T }
      | undefined;
    return hit ? hit.value : fallback;
  }
  const rec = props as Record<string, T>;
  return name in rec ? rec[name] : fallback;
}

/** Tiled writes the class under `type` (<1.9) and/or `class` (>=1.9). Prefer that
 *  explicit class field so an object can carry a human label in `name` (e.g. an
 *  `upgrade` named "watch"); fall back to `name` for older maps that mirror the
 *  class into the name instead. */
export function objClass(obj: TiledObject): string {
  const anyObj = obj as unknown as { name?: string; type?: string; class?: string };
  return anyObj.type || anyObj.class || anyObj.name || "";
}

/** Whether a Tiled tile object was flipped horizontally in the editor. Phaser
 *  parses the gid's flip flags onto the object (`flippedHorizontal`) and strips
 *  the gid; fall back to reading the raw high bit off the gid just in case. So a
 *  designer can face a placeholder (e.g. the PE guy) by flipping it in Tiled. */
export function objFlippedH(obj: TiledObject): boolean {
  const anyObj = obj as unknown as { flippedHorizontal?: boolean; gid?: number };
  if (typeof anyObj.flippedHorizontal === "boolean") return anyObj.flippedHorizontal;
  return !!(anyObj.gid && ((anyObj.gid >>> 0) & 0x80000000));
}

/**
 * Shared level engine: everything that reads a Tiled map lives here so the
 * three (very different) level scenes only implement their own mechanics.
 */
export abstract class BaseLevelScene extends Phaser.Scene {
  protected levelId?: LevelId; // undefined for the standalone sample level
  protected title = "";
  protected levelHint = ""; // HUD hint authored via the map `hint` property (optional)
  protected playerTex = "";
  protected persist = true; // whether completion writes to GameState

  protected map!: Phaser.Tilemaps.Tilemap;
  protected world!: Phaser.Tilemaps.TilemapLayer;
  protected midFloor?: Phaser.Physics.Arcade.StaticGroup; // mid-anchor tile bodies (feet on tile middle)
  protected worldFirstgid = 1; // office tileset firstgid, for local-id tile frame lookups
  protected player!: Phaser.Physics.Arcade.Sprite;

  protected collectibles!: Phaser.Physics.Arcade.Group;
  protected doors: DoorRec[] = [];
  protected hazardTiles: number[] = [];

  protected score = 0;
  // Running total split by source, surfaced on the results card breakdown.
  protected scoreParts = { collectibles: 0, enemies: 0, bonus: 0, speed: 0, sheepy: 0 };
  // Combo batch: hits from one thrown projectile, keyed by the projectile
  // GameObject and scored (with the combo/throw multiplier) when it is spent.
  private comboBatch = new Map<
    Phaser.GameObjects.GameObject,
    { base: number; enemy: boolean }[]
  >();
  // Wall-clock start of a timed level (ms, from this.time.now), for speed bonus.
  protected levelStartMs = 0;
  protected collectedCount = 0;
  protected totalCollectibles = 0;
  protected foundSheepyThisRun = false;
  protected finished = false;
  // Scene-clock timestamp (ms) when the level world was built. The Phaser scene
  // clock only advances while this scene runs (menus, level-select and cutscenes
  // are separate scenes, and pause/resume is unused), so `time.now - levelStartAt`
  // at completion is the active play time inside the level. See recordLevelResult.
  protected levelStartAt = 0;

  protected keys!: {
    cursors: Phaser.Types.Input.Keyboard.CursorKeys;
    left: Phaser.Input.Keyboard.Key;
    right: Phaser.Input.Keyboard.Key;
    up: Phaser.Input.Keyboard.Key;
    down: Phaser.Input.Keyboard.Key;
    jump: Phaser.Input.Keyboard.Key;
    throw: Phaser.Input.Keyboard.Key;
  };

  // The HUD is authored in Tiled (public/assets/maps/hud.tmj): text objects named
  // "score", "info" and "timer" set the position, font and colour of each cluster.
  // Their content is filled in here (score/collectibles table in base; the status
  // line and countdown per level), and the dark bar + marquee notices are code-drawn.
  private hudScore?: Phaser.GameObjects.Text; // left: Score / Collectibles table
  private hudInfo?: Phaser.GameObjects.Text; // right: the level's status line / table
  private hudTimer?: Phaser.GameObjects.Text; // right: big yellow countdown (timed levels)
  private hudTimerBaseColor = HUD_YELLOW; // authored timer colour, restored after a warn
  private notice?: Phaser.GameObjects.Text; // single on-bar message slot
  private noticeTween?: Phaser.Tweens.Tween; // marquee pan for an over-wide notice
  private noticeFade?: Phaser.Time.TimerEvent; // fires the notice's fade-out

  protected mapKey = "";

  constructor(sceneKey: string) {
    super(sceneKey);
  }

  // ---- lifecycle the subclass composes ------------------------------------

  /** Build a level from its metadata (records stars/sheepies to GameState). */
  protected buildWorld(meta: LevelMeta) {
    this.levelId = meta.id;
    this.persist = true;
    this.initTilemap(meta.mapKey, meta.playerTex, meta.title);
  }

  /** Build an unregistered level directly from a map key - used by the sample
   *  level so it can run without touching progression. */
  protected buildWorldRaw(mapKey: string, playerTex: string, title: string) {
    this.levelId = undefined;
    this.persist = false;
    this.initTilemap(mapKey, playerTex, title);
  }

  private initTilemap(mapKey: string, playerTex: string, title: string) {
    this.mapKey = mapKey;
    this.playerTex = playerTex;
    this.title = title;
    this.levelHint = "";
    this.finished = false;
    this.score = 0;
    this.scoreParts = { collectibles: 0, enemies: 0, bonus: 0, speed: 0, sheepy: 0 };
    this.comboBatch.clear();
    this.collectedCount = 0;
    this.totalCollectibles = 0;
    this.foundSheepyThisRun = false;
    this.levelStartAt = this.time.now;
    this.doors = [];

    this.map = this.make.tilemap({ key: mapKey });
    // First arg matches the tileset `name` in the .tmj; second is the image
    // cache key we loaded in PreloadScene. This is the join between Tiled and
    // Phaser - it does NOT use the image path stored in the map file.
    // Use the tileset name the map actually embeds (e.g. "office"), falling back
    // to the historical "placeholder". The second arg is the loaded image key.
    const tsName = this.map.tilesets[0]?.name ?? TILESET_NAME;
    const tileset = this.map.addTilesetImage(tsName, TILESET_KEY)!;
    // createLayer can return null (unknown layer name); the cast asserts the
    // "world" layer exists, which it does in every map we author.
    this.world = this.map.createLayer("world", tileset, 0, 0) as Phaser.Tilemaps.TilemapLayer;
    this.world.setVisible(false);

    // Collision is data-driven: any tile whose tileset property `collides` is
    // true becomes solid. Level designers never touch code to add geometry.
    this.world.setCollisionByProperty({ [Prop.COLLIDES]: true });
    // One-way / jump-through tiles (property `oneway`): also collidable, but the
    // player-vs-world collider (createPlayer) only lets them stop the player when
    // landing from above. Great at the top of a ladder so you can step off it.
    this.world.setCollisionByProperty({ [Prop.ONEWAY]: true });
    // Mid-floor tiles (the last office tile): solid, but anchor the player on the tile's
    // vertical middle. Their bodies live in a static group; collider added in createPlayer.
    this.worldFirstgid = tileset.firstgid;
    this.midFloor = buildMidFloorBodies(this, this.world, tileset.firstgid);

    // Visual-only platforms layer (decorative, no collision).
    const platformsTs = this.map.addTilesetImage("platforms", "platforms-tiles");
    if (platformsTs) this.map.createLayer("platforms", [tileset, platformsTs], 0, 0)?.setDepth(1);

    this.collectibles = this.physics.add.group({ allowGravity: false, immovable: true });

    this.physics.world.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);

    // Level identity can be authored in Tiled via Map custom properties, so it
    // lives with the level rather than hardcoded in the scene. Dynamic HUD values
    // (score, collected count, throwable state) still come from code.
    this.title = this.mapProp(Prop.TITLE, title);
    this.levelHint = this.mapProp(Prop.HINT, "");

    // Decorative content placed in Tiled (background image layers, text objects,
    // and tile/decor objects) renders here. It's non-interactive; gameplay objects
    // are still handled by each scene's forEachObject switch. Shared with the menu
    // screens via tiledRender so authoring works identically everywhere.
    renderTiledAuthoredContent(this, this.map, mapKey);
  }

  /** Greybox visibility for one-way (jump-through) platforms. Every level hides its
   *  "world" collision layer and draws its floors from the painted background art, so a
   *  bare `oneway` collision tile is invisible (the player would land on thin air). In
   *  levels whose art does not yet paint these platforms, call this AFTER hiding the
   *  world layer: it draws each `oneway` tile as a visible placeholder sprite (from the
   *  tileset spritesheet) at the collision tile's exact spot, depth just under the
   *  player. When the platform gets painted into the background art later, drop the call. */
  protected revealOnewayPlatforms() {
    if (!this.textures.exists(TILESET_SHEET_KEY)) return;
    this.world.forEachTile((t) => {
      if (!t.properties || !(t.properties as Record<string, unknown>)[Prop.ONEWAY]) return;
      this.add
        .image(t.pixelX, t.pixelY, TILESET_SHEET_KEY, t.index - this.worldFirstgid)
        .setOrigin(0, 0)
        .setDepth(4);
    });
  }

  // ---- authored (non-interactive) content from Tiled -----------------------

  /** Read a Map-level custom property, tolerant of the array/object forms. */
  protected mapProp<T>(name: string, fallback: T): T {
    const props = (this.map as unknown as { properties?: unknown }).properties;
    if (!props) return fallback;
    if (Array.isArray(props)) {
      const hit = props.find((p) => (p as { name: string }).name === name) as { value: T } | undefined;
      return hit ? hit.value : fallback;
    }
    const rec = props as Record<string, T>;
    return name in rec ? rec[name] : fallback;
  }

  /** Walk the "objects" layer and hand each object to the subclass. Common
   *  object classes (collectible, sheepy) can be handled with the helpers below. */
  protected forEachObject(handler: (obj: TiledObject, cls: string) => void) {
    const layer = this.map.getObjectLayer("objects");
    if (!layer) return;
    for (const obj of layer.objects) handler(obj, objClass(obj));
  }

  protected findSpawn(): { x: number; y: number } {
    const layer = this.map.getObjectLayer("objects");
    const spawn = layer?.objects.find((o) => objClass(o) === "spawn");
    return { x: spawn?.x ?? 64, y: spawn?.y ?? 64 };
  }

  protected createPlayer(x: number, y: number): Phaser.Physics.Arcade.Sprite {
    this.player = this.physics.add.sprite(x, y, this.playerTex);
    this.player.setCollideWorldBounds(true);
    // Depth layering: image layer (-10) < world (0) < decor (4) < player (5) <
    // text (6) < HUD (1000). Keeps decor props behind the player.
    this.player.setDepth(5);
    // Player vs world. Solid tiles always collide; `oneway` tiles only stop the
    // player when they are falling onto the tile from above (so they can jump/climb
    // up through them). See setCollisionByProperty(oneway) in buildWorldRaw.
    this.physics.add.collider(this.player, this.world, undefined, (_player, tileObj) => {
      const tile = tileObj as Phaser.Tilemaps.Tile;
      if (!tile.properties || !(tile.properties as Record<string, unknown>)[Prop.ONEWAY]) return true;
      if (this.downDown()) return false; // hold down to drop through a one-way platform
      const body = this.player.body as Phaser.Physics.Arcade.Body;
      // Land only when moving down and the player's feet were above the tile top last frame.
      return body.velocity.y >= 0 && body.prev.y + body.height <= tile.pixelY + 4;
    });
    // Mid-floor bodies: top edge at the tile's middle (player rests half a tile lower), and
    // ONE-WAY like the jump-through platform: land from above, jump up through, hold down to
    // drop through, and climb through on a ladder (Level2 overrides isClimbing()).
    if (this.midFloor) {
      this.physics.add.collider(this.player, this.midFloor, undefined, (_player, zoneObj) => {
        if (this.isClimbing()) return false; // climbing a ladder passes straight through
        if (this.downDown()) return false; // hold down to drop through
        const body = this.player.body as Phaser.Physics.Arcade.Body;
        const zbody = (zoneObj as Phaser.GameObjects.Zone).body as Phaser.Physics.Arcade.StaticBody;
        return body.velocity.y >= 0 && body.prev.y + body.height <= zbody.top + 4;
      });
    }
    return this.player;
  }

  // ---- shared object factories --------------------------------------------

  protected spawnCollectible(obj: TiledObject) {
    const kind = objProp(obj, Prop.KIND, "money");
    const value = objProp(obj, Prop.VALUE, baseValueForKind(kind));
    const c = this.collectibles.create(obj.x!, obj.y!, collectibleTex(kind)) as Phaser.Physics.Arcade.Sprite;
    // Collectibles are all real art (~64px source); normalize per-kind so laptop/VW
    // read bigger than the rest (see COLLECTIBLE_PX).
    c.setScale(collectiblePx(kind) / Math.max(c.width, c.height));
    if (kind === "money") c.setPosition(obj.x!, obj.y! + 2); // nudge down so the bag sits on the surface
    c.setData("kind", kind);
    c.setData("value", value);
    this.totalCollectibles++;
    this.physics.add.overlap(this.player, c, () => this.collectSprite(c));
    return c;
  }

  protected spawnSheepy(obj: TiledObject) {
    ensureSheepyAnim(this);
    const s = this.physics.add.sprite(obj.x!, obj.y!, Tex.sheepy).setScale(0.56); // ~28px (frames are 50px)
    s.play(SHEEPY_ANIM);
    (s.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    this.tweens.add({ targets: s, y: s.y - 6, duration: 700, yoyo: true, repeat: -1 });
    this.physics.add.overlap(this.player, s, () => {
      if (!s.active) return;
      s.destroy();
      this.foundSheepyThisRun = true;
      this.score += SHEEPY_BONUS;
      this.scoreParts.sheepy += SHEEPY_BONUS;
      if (this.persist && this.levelId) GameState.foundSheepy(this.levelId);
      this.refreshHud();
      this.flashText(`★ Sheepy! +${SHEEPY_BONUS} ★`, Palette.text);
    });
    return s;
  }

  /**
   * A door barrier at a Tiled `door` object. It's a SHORT door (2 tiles tall)
   * that sits in a DOORWAY - the map should build a wall around/above it (with a
   * 2-tile gap at the floor) so it can't be jumped over. Doors can OPEN and
   * CLOSE (a plate held by a box keeps its door open); opening disables the
   * body + fades the sprite, closing re-enables it. The door object sits on the
   * walking-surface tile of whatever floor the doorway is on.
   */
  protected makeDoor(obj: TiledObject, opensWith: string): DoorRec {
    const DOOR_TILES = 2;
    const groundY = obj.y! + 16; // bottom of the door's tile = the floor line
    const h = DOOR_TILES * 32;
    const sprite = this.physics.add.staticImage(obj.x!, groundY - h / 2, Tex.door);
    sprite.setDisplaySize(26, h).refreshBody();
    const rec: DoorRec = {
      opensWith,
      sprite,
      colliders: [this.physics.add.collider(this.player, sprite)],
      opened: false,
      x: obj.x!,
      counted: false,
    };
    this.doors.push(rec);
    return rec;
  }

  /** Make each door also block the given bodies (e.g. pushable boxes), so they
   *  can't be shoved through a shut door. Collisions follow the door's body
   *  enable state, so they stop mattering while the door is open. */
  protected blockDoorsWith(bodies: Phaser.GameObjects.GameObject[]) {
    for (const door of this.doors) {
      for (const b of bodies) door.colliders.push(this.physics.add.collider(b, door.sprite));
    }
  }

  /** Open or close one door (idempotent). Open = body disabled + faded. */
  protected setDoorOpen(door: DoorRec, open: boolean) {
    if (door.opened === open) return;
    door.opened = open;
    (door.sprite.body as Phaser.Physics.Arcade.StaticBody).enable = !open;
    door.sprite.setAlpha(open ? 0.18 : 1);
  }

  /** Latch open every closed door matching `token` (e.g. the cracked vault).
   *  Returns how many opened. For hold-to-open plates use setDoorOpen directly. */
  protected openDoors(token: string): number {
    let opened = 0;
    for (const door of this.doors) {
      if (door.opened || door.opensWith !== token) continue;
      this.setDoorOpen(door, true);
      opened++;
    }
    return opened;
  }

  protected doorFor(token: string): DoorRec | undefined {
    return this.doors.find((d) => d.opensWith === token);
  }

  /** Collect a specific collectible sprite (used by both walk-over overlap and
   *  Dan's thrown-projectile hits). Safe to call twice on the same sprite.
   *  A manual collect scores its base immediately; a thrown collect is batched
   *  onto its projectile and scored (with the combo/throw multiplier) when the
   *  projectile is spent (see resolveThrow). */
  protected collectSprite(
    c: Phaser.Physics.Arcade.Sprite,
    projectile?: Phaser.GameObjects.GameObject,
  ) {
    if (!c.active) return;
    const kind = (c.getData("kind") as string) ?? "money";
    const base = (c.getData("value") as number) ?? baseValueForKind(kind);
    c.destroy();
    this.collectedCount++;
    if (projectile) {
      this.recordThrowHit(projectile, base, false);
    } else {
      this.score += base;
      this.scoreParts.collectibles += base;
    }
    this.refreshHud();
  }

  /** Record a hit from a thrown projectile; scored later in resolveThrow. */
  protected recordThrowHit(
    p: Phaser.GameObjects.GameObject,
    base: number,
    enemy: boolean,
  ) {
    const b = this.comboBatch.get(p) ?? [];
    b.push({ base, enemy });
    this.comboBatch.set(p, b);
  }

  /** Score everything one projectile hit, apply the combo/throw multiplier, then
   *  destroy it. Safe to call on a projectile with no recorded hits (just
   *  destroys it). */
  protected resolveThrow(p: Phaser.GameObjects.GameObject) {
    const b = this.comboBatch.get(p);
    if (b && b.length) {
      const count = b.length;
      const mult = throwMultiplier(count);
      let baseSum = 0;
      for (const hit of b) {
        baseSum += hit.base;
        if (hit.enemy) this.scoreParts.enemies += hit.base;
        else this.scoreParts.collectibles += hit.base;
      }
      const awarded = baseSum * mult;
      this.scoreParts.bonus += baseSum * (mult - 1);
      this.score += awarded;
      this.refreshHud();
      if (count >= 2) this.flashText(`Combo x${count}!  +${awarded}`, Palette.text);
      if (import.meta.env.DEV) console.log(`[score] throw hit ${count} -> x${mult} (+${awarded})`);
    }
    this.comboBatch.delete(p);
    (p as Phaser.GameObjects.GameObject & { destroy(): void }).destroy();
  }

  /** Seconds elapsed since levelStartMs (set by timed levels). */
  protected elapsedSeconds(): number {
    return (this.time.now - this.levelStartMs) / 1000;
  }

  /** Apply a speed bonus for `levelKey`/`elapsed` into both score and parts. */
  protected applySpeedBonus(levelKey: string, elapsed: number): number {
    const b = speedBonus(levelKey, elapsed);
    this.score += b;
    this.scoreParts.speed += b;
    return b;
  }

  // ---- HUD -----------------------------------------------------------------

  /**
   * Build the HUD. A dark translucent bar (code-drawn) is pinned across the top so
   * the readouts stay legible over the level art. The score / status / timer text is
   * authored in Tiled (hud.tmj) as objects named "score", "info" and "timer"; we
   * keep references to those, style and all, and fill their content here. Each
   * cluster reads as a small two-column table (label left, value right), aligned
   * with monospace padding. No level title, no dev-controls footer.
   */
  protected buildHud() {
    const W = this.scale.width;

    // Reset per-run handles (the scene instance is reused across restarts).
    this.hudScore = undefined;
    this.hudInfo = undefined;
    this.hudTimer = undefined;
    this.hudTimerBaseColor = HUD_YELLOW;
    this.notice = undefined;
    this.noticeTween = undefined;
    this.noticeFade = undefined;

    // The 80%-opaque bar behind everything else in the HUD.
    this.add
      .rectangle(0, 0, W, HUD_BAR_H, 0x000000, 0.8)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(999);

    // Tiled-authored HUD text: position, font and colour come from hud.tmj; content
    // is filled by refreshHud / setHudInfo / setHudTimer below.
    for (const o of this.hudLayerObjects()) {
      const txt = createTiledText(this, o).setScrollFactor(0).setDepth(1000);
      const name = String((o.name as string) ?? "").toLowerCase();
      if (name === "score") this.hudScore = txt;
      else if (name === "info") this.hudInfo = txt;
      else if (name === "timer") {
        this.hudTimer = txt;
        this.hudTimerBaseColor = (txt.style.color as string) || HUD_YELLOW;
        txt.setText(""); // stays empty until a timed level calls setHudTimer
      }
    }

    // Fallbacks if hud.tmj is missing a score / info object.
    if (!this.hudScore) {
      this.hudScore = this.add
        .text(12, HUD_BAR_H / 2, "", { fontFamily: "monospace", fontSize: "13px", color: Palette.text })
        .setOrigin(0, 0.5)
        .setScrollFactor(0)
        .setDepth(1000);
    }
    if (!this.hudInfo) {
      this.hudInfo = this.add
        .text(W - 16, HUD_BAR_H / 2, "", { fontFamily: "monospace", fontSize: "13px", color: Palette.textDim, align: "right" })
        .setOrigin(1, 0.5)
        .setScrollFactor(0)
        .setDepth(1000);
    }

    this.refreshHud();

    // DEV-only verification affordance: expose the live scene so the Playwright
    // harness can read score / scoreParts / collectedCount and drive scenarios.
    // Guarded so it never ships in a production build.
    if (import.meta.env.DEV) (window as unknown as { __level?: unknown }).__level = this;
  }

  /** Lay out label/value rows as a monospace table: labels flush left, values flush
   *  right in a shared column. Works whether the text object is left- or right-
   *  aligned, because every line is padded to the same character width. */
  protected hudTable(rows: Array<[string, string]>): string {
    const width = Math.max(...rows.map(([label, value]) => label.length + value.length)) + 2;
    return rows.map(([label, value]) => label + value.padStart(width - label.length)).join("\n");
  }

  /** Show or update the big yellow countdown. Timed levels call this; untimed ones
   *  never do. `warn` turns it red; otherwise it uses the authored colour. */
  protected setHudTimer(text: string, warn = false) {
    if (!this.hudTimer) {
      this.hudTimer = this.add
        .text(this.scale.width - 16, HUD_BAR_H / 2, "", {
          fontFamily: "monospace",
          fontSize: "26px",
          fontStyle: "bold",
          color: HUD_YELLOW,
        })
        .setOrigin(1, 0.5)
        .setScrollFactor(0)
        .setDepth(1000);
    }
    this.hudTimer.setText(text).setColor(warn ? "#ff5a5a" : this.hudTimerBaseColor);
  }

  /** Set the level's status line (the right cluster). Levels build it with hudTable
   *  for the two-column look, e.g. Lucas's Bionic / batteries. */
  protected setHudInfo(text: string) {
    this.hudInfo?.setText(text);
  }

  protected refreshHud() {
    const rows: Array<[string, string]> = [["Score", String(this.score)]];
    // Only show the collectibles row where the level actually has collectibles.
    if (this.totalCollectibles > 0) {
      rows.push(["Collectibles", `${this.collectedCount} of ${this.totalCollectibles}`]);
    }
    this.hudScore?.setText(this.hudTable(rows));
  }

  /** HUD text objects to render. A level's own "hud" object layer wins (per-level
   *  override); otherwise the shared HUD map's objects are used on every level. */
  private hudLayerObjects(): Array<Record<string, unknown>> {
    const own = this.hudTextObjects(this.mapKey, (l) => l.name === "hud");
    return own.length ? own : this.hudTextObjects(SHARED_HUD_KEY, () => true);
  }

  /** Text objects from the object layers of a cached map that match `pickLayer`. */
  private hudTextObjects(
    mapKey: string,
    pickLayer: (l: Record<string, unknown>) => boolean,
  ): Array<Record<string, unknown>> {
    const raw = this.cache.tilemap.get(mapKey)?.data as
      | { layers?: Array<Record<string, unknown>> }
      | undefined;
    const out: Array<Record<string, unknown>> = [];
    for (const l of raw?.layers ?? []) {
      if (l.type !== "objectgroup" || !pickLayer(l)) continue;
      for (const o of (l.objects as Array<Record<string, unknown>>) ?? []) {
        if (o.text && o.visible !== false) out.push(o);
      }
    }
    return out;
  }

  /**
   * Pop a transient notice on the HUD bar, drawn on top of it so it reads clearly
   * against the dark strip rather than the busy level art. It lives in the open lane
   * between the score (left) and the status / timer cluster (right), so it never
   * sits over those readouts. There is a single slot: a new message replaces
   * whatever is showing. A line too wide for the lane marquees back and forth,
   * clipped to the lane so its ends never spill onto the readouts.
   */
  protected flashText(message: string, color: number | string = Palette.text) {
    const css = typeof color === "number" ? Phaser.Display.Color.IntegerToColor(color).rgba : color;

    this.noticeTween?.remove();
    this.noticeFade?.remove();
    this.notice?.destroy();

    // The clear lane: right of the score, left of the status/timer cluster.
    const scoreRight = this.hudScore ? this.hudScore.x + this.hudScore.width : 16;
    let clusterLeft = this.scale.width - 16;
    if (this.hudTimer) clusterLeft = Math.min(clusterLeft, this.hudTimer.x - this.hudTimer.width);
    if (this.hudInfo?.text) clusterLeft = Math.min(clusterLeft, this.hudInfo.x - this.hudInfo.width);
    const laneL = scoreRight + 16;
    const laneW = Math.max(160, clusterLeft - 16 - laneL);

    const t = this.add
      .text(laneL, HUD_BAR_H / 2, message, {
        fontFamily: "monospace",
        fontSize: "18px",
        color: css,
      })
      .setOrigin(0, 0.5)
      .setScrollFactor(0)
      .setDepth(1002)
      .setShadow(0, 1, "#000000", 3);
    this.notice = t;

    let hold = 3600;
    if (t.width <= laneW) {
      // Fits the lane: centre it there.
      t.setX(laneL + (laneW - t.width) / 2);
    } else {
      // Too wide: marquee back and forth, cropped to the lane so the ends stay off
      // the score and the timer. Moving x by -offset while cropping from +offset
      // keeps a fixed viewport at the lane while the text scrolls through it.
      const maxOffset = t.width - laneW;
      const pan = { o: 0 };
      t.setCrop(0, 0, laneW, t.height);
      this.noticeTween = this.tweens.add({
        targets: pan,
        o: maxOffset,
        duration: Math.max(1500, maxOffset * 14),
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
        onUpdate: () => {
          t.setX(laneL - pan.o);
          t.setCrop(pan.o, 0, laneW, t.height);
        },
      });
      hold = 7000;
    }

    this.noticeFade = this.time.delayedCall(hold, () => {
      this.noticeTween?.remove();
      this.tweens.add({
        targets: t,
        alpha: 0,
        duration: 420,
        onComplete: () => {
          t.destroy();
          if (this.notice === t) this.notice = undefined;
        },
      });
    });
  }

  // ---- input ---------------------------------------------------------------

  protected buildInput() {
    const kb = this.input.keyboard!;
    this.keys = {
      cursors: kb.createCursorKeys(),
      left: kb.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      right: kb.addKey(Phaser.Input.Keyboard.KeyCodes.D),
      up: kb.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      down: kb.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      jump: kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE),
      throw: kb.addKey(Phaser.Input.Keyboard.KeyCodes.F),
    };

    kb.on("keydown-ESC", () => this.scene.start("LevelSelect"));
    kb.on("keydown-N", () => this.completeLevel(3, ["(dev) forced completion"]));
  }

  protected leftDown() {
    return this.keys.cursors.left?.isDown || this.keys.left.isDown;
  }
  protected rightDown() {
    return this.keys.cursors.right?.isDown || this.keys.right.isDown;
  }
  protected upDown() {
    return this.keys.cursors.up?.isDown || this.keys.up.isDown;
  }
  protected downDown() {
    return this.keys.cursors.down?.isDown || this.keys.down.isDown;
  }
  /** Whether the player is currently climbing a ladder. Used so mid-floor (and other
   *  jump-through) tiles let a ladder climb pass through. Levels with ladders override. */
  protected isClimbing(): boolean {
    return false;
  }
  protected jumpJustDown() {
    return (
      Phaser.Input.Keyboard.JustDown(this.keys.jump) ||
      Phaser.Input.Keyboard.JustDown(this.keys.cursors.up!)
    );
  }
  protected throwJustDown() {
    return Phaser.Input.Keyboard.JustDown(this.keys.throw);
  }

  // ---- completion ----------------------------------------------------------

  protected completeLevel(stars: number, lines: string[]) {
    if (this.finished) return;
    this.finished = true;
    if (!this.levelId) return this.showBanner("Sample complete!", lines);
    const wasLocked = !GameState.finaleUnlocked();
    if (this.persist) {
      GameState.recordStars(this.levelId, stars);
      GameState.recordLevelResult(this.levelId, {
        score: this.score,
        collectibles: this.collectedCount,
        timeMs: this.time.now - this.levelStartAt,
      });
    }
    const regionConquered = wasLocked && GameState.finaleUnlocked();
    this.scene.start("LevelResult", {
      levelId: this.levelId,
      stars,
      lines,
      sheepy: this.foundSheepyThisRun || GameState.hasSheepy(this.levelId),
      score: { ...this.scoreParts, total: this.score },
      regionConquered,
    });
  }

  protected failLevel(_reason: string) {
    if (this.finished) return;
    this.finished = true;
    if (!this.levelId) return this.showBanner("Sample - restarting", [_reason]);
    const meta = levelById(this.levelId);
    this.physics.pause();
    this.cameras.main.fade(600, 0, 0, 0, false, (_cam: unknown, progress: number) => {
      if (progress >= 1) this.scene.start("GameOver", { returnScene: meta.sceneKey });
    });
  }

  /** In-scene end card for the standalone sample level (no progression). */
  private showBanner(heading: string, lines: string[]) {
    const { width, height } = this.scale;
    this.physics.pause();
    this.add.rectangle(width / 2, height / 2, width, height, Palette.bg, 0.8).setScrollFactor(0).setDepth(2000);
    this.add
      .text(width / 2, height / 2 - 30, heading, { fontFamily: "monospace", fontSize: "30px", color: Palette.text })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(2001);
    this.add
      .text(width / 2, height / 2 + 30, [...lines, "", "ESC / R - back to menu"].join("\n"), {
        fontFamily: "monospace",
        fontSize: "15px",
        color: Palette.textDim,
        align: "center",
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(2001);
    this.input.keyboard!.once("keydown-R", () => this.scene.start("LevelSelect"));
  }
}
